/**
 * Token Refresh Modal
 * Shows when JWT token is about to expire, giving user option to refresh
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
} from '@ionic/react';

interface TokenRefreshModalProps {
  isOpen: boolean;
  onRefresh: () => void;
  onExpired: () => void;
  timeRemaining: number; // seconds until expiration
  onOpenVerification: () => void;
}

const TokenRefreshModal: React.FC<TokenRefreshModalProps> = ({
  isOpen,
  onRefresh,
  onExpired,
  timeRemaining,
  onOpenVerification,
}) => {
  const [countdown, setCountdown] = useState(timeRemaining);

  const handleExpired = useCallback(() => {
    // User didn't respond in time
    onExpired();
  }, [onExpired]);

  // Update countdown
  useEffect(() => {
    if (!isOpen) {
      // Stop countdown if modal is closed
      return;
    }

    const interval = setInterval(() => {
      setCountdown(prev => {
        const newCountdown = prev - 1;
        if (newCountdown <= 0) {
          clearInterval(interval);
          handleExpired();
          return 0;
        }
        return newCountdown;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, handleExpired]);

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(timeRemaining);
    }
  }, [isOpen, timeRemaining]);

  const handleRefresh = () => {
    // Close this modal, reset timer, and open UserVerificationModal
    onRefresh(); // This resets the timer in TokenExpirationMonitor
    onOpenVerification(); // This opens UserVerificationModal in parent
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) {
      return `${mins} minute${mins !== 1 ? 's' : ''}`;
    }
    return `${mins}m ${secs}s`;
  };

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false} canDismiss={!isOpen}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Session Expiring Soon</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <IonText>
            <h2>Your session is about to expire</h2>
            <p>
              You have <strong>{formatTime(countdown)}</strong> to confirm you
              are still here.
            </p>
            <p>
              Click &quot;Verify Your Account&quot; to verify your identity and
              refresh your session.
            </p>
          </IonText>

          <div style={{ marginTop: '2rem' }}>
            <IonButton
              expand="block"
              onClick={handleRefresh}
              disabled={countdown <= 0}
              size="large"
            >
              Verify Your Account
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default TokenRefreshModal;
